import { ReactNode, useMemo, useState } from "react";

import Breadcrumb from "./Breadcrumb";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";

import { useAuthStore } from "@type/store";
import { useLayoutStore } from "@type/store/LayoutStore";
import { useRenderStatus } from "hooks/useRenderStatus";
import cn from "lib/cn";

import { SIDEBAR_WIDTH } from "./constants";
import { MAIN_LAYOUT_ID } from "constants/index";

export interface LayoutProps {
  children: ReactNode;
  id?: string;
}

const Layout = ({ children, id }: LayoutProps) => {
  const busy = useRenderStatus(500);
  const showBaseLayout = useLayoutStore((state) => state.showBaseLayout);
  const pageAction = useLayoutStore((state) => state.pageAction);
  const accessDenied = useLayoutStore((state) => state.accessDenied);
  const showDimBackground = useLayoutStore((state) => state.showDimBackground);
  const isDataNotFound = useLayoutStore((state) => state.isDataNotFound);

  const isLogin = useAuthStore((state) => state.isLogin);

  const [collapsed, setSidebarCollapsed] = useState(true);

  const handleClickBackdrop = () => {
    setSidebarCollapsed(true);
  };

  const showNavItem = useMemo(() => {
    return !accessDenied && !isDataNotFound && !busy;
  }, [accessDenied, isDataNotFound, busy]);

  if (!showBaseLayout || !isLogin) return <>{children}</>;

  return (
    <div
      data-testid={id}
      id={id}
      className="relative min-h-screen bg-neutral-gray-400"
    >
      {!collapsed && (
        // backdrop
        <div
          data-tesid={id ? `${id}-backdrop` : undefined}
          onClick={handleClickBackdrop}
          className="absolute w-full top-0 bottom-0 cursor-pointer z-30"
        />
      )}
      <Navbar
        id={id ? `${id}-navbar` : undefined}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      {showNavItem ? (
        <div className="bg-white">
          <div className="ml-[6rem] bg-white flex gap-8 justify-between mr-[2rem]">
            <Breadcrumb />
            {pageAction}
          </div>
        </div>
      ) : (
        <div className="h-[3rem]" />
      )}
      <div
        data-testid={id ? `${id}-sidebar-container` : undefined}
        className={cn([
          "h-screen min-h-full absolute bottom-0 top-0 z-[100] overflow-x-hidden",
          collapsed ? "w-[4rem]" : "w-[18rem]",
          "transition-[width] duration-300 ease-in-out",
        ])}
      >
        <Sidebar
          id={id ? `${id}-sidebar` : undefined}
          collapsed={collapsed}
          setCollapsed={setSidebarCollapsed}
        />
      </div>
      <div
        id={MAIN_LAYOUT_ID}
        style={{ marginLeft: SIDEBAR_WIDTH }}
        className="relative overflow-y-auto h-screen max-h-main bg-gray-600 scrollBar"
      >
        {showDimBackground && (
          <div
            style={{ marginLeft: SIDEBAR_WIDTH }}
            className="fixed max-h-[calc(100vh-4rem)] bg-black/30 h-screen bottom-0 left-0 right-0 z-[50]"
          />
        )}
        <div className="px-[2rem] pt-[1.75rem] pb-[1rem] h-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
